import { Controller } from "@hotwired/stimulus"
import { createLogger } from '../utils/logger'

export default class extends Controller {
  static targets = [
    "iframe",
    "previewContainer", 
    "devtoolsContainer",
    "panelsContainer",
    "tab",
    "consolePanel",
    "networkPanel", 
    "domPanel",
    "performancePanel",
    "errorsPanel",
    "consoleBadge",
    "networkBadge",
    "errorsBadge",
    "urlBar",
    "backButton",
    "forwardButton",
    "loadingIndicator",
    "allSparkFilterToggle"
  ]

  static values = {
    iframeUrl: String,
    buildSessionId: String,
    initialPanel: { type: String, default: "console" },
    navigationHistory: { type: Array, default: [] },
    historyIndex: { type: Number, default: -1 }
  }

  connect() {
    this.logger = createLogger('preview-devtools')
    console.log('PreviewDevtools controller connected')
    
    this.setupIframe()
    this.setupMessageBridge()
    this.initializeNavigation()
    
    // Show initial panel after a brief delay to ensure DOM is ready
    setTimeout(() => {
      this.showPanel(this.initialPanelValue)
      // Test console functionality
      this.testConsoleIntegration()
    }, 100)
  }

  disconnect() {
    this.teardownMessageBridge()
    this.teardownMonitoring()
  }

  teardownMonitoring() {
    // Clear monitoring intervals
    if (this.domMonitoringInterval) {
      clearInterval(this.domMonitoringInterval)
      this.domMonitoringInterval = null
    }
    if (this.performanceMonitoringInterval) {
      clearInterval(this.performanceMonitoringInterval)
      this.performanceMonitoringInterval = null
    }
    // Reset initialization flag
    this.monitoringInitialized = false
  }

  setupIframe() {
    if (this.hasIframeTarget) {
      this.iframeTarget.addEventListener('load', this.handleIframeLoad.bind(this))
    }
  }

  async handleIframeLoad() {
    console.log('Iframe loaded:', this.iframeTarget.src)
    
    // Prevent handling multiple load events for the same URL
    if (this.lastLoadedUrl === this.iframeTarget.src && this.monitoringInitialized) {
      console.log('Same URL already loaded and monitoring initialized, skipping...')
      return
    }
    this.lastLoadedUrl = this.iframeTarget.src
    
    // Check if the target supports DevTools
    const supportsDevTools = await this.checkDevToolsSupport()
    if (!supportsDevTools) {
      console.warn('Target application does not support DevTools monitoring')
      // Show a message in the DevTools panels about configuration needed
      this.showConfigurationMessage()
      return
    }
    
    // Initialize postMessage-based monitoring
    setTimeout(() => {
      this.initializePostMessageMonitoring()
    }, 100)
    
    this.dispatch('iframe-loaded', { detail: { url: this.iframeTarget.src } })
  }

  setupMessageBridge() {
    this.messageHandler = this.handleMessage.bind(this)
    window.addEventListener('message', this.messageHandler)
  }

  teardownMessageBridge() {
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler)
    }
  }

  handleMessage(event) {
    // Log all messages for debugging
    console.log('PreviewDevTools: Raw message received:')
    console.log('  Origin:', event.origin)
    console.log('  Data:', event.data)
    console.log('  Type:', event.data?.type)
    console.log('  Source:', event.data?.source)
    
    // Validate origin
    if (!this.validateMessageOrigin(event.origin)) {
      console.debug('PreviewDevTools: Message from invalid origin:', event.origin)
      return
    }

    const { type, data } = event.data || {}
    console.log('PreviewDevTools: Processing message:', { type, origin: event.origin })

    switch (type) {
      // Legacy message types (keep for compatibility)
      case 'console':
        this.handleConsoleMessage(data)
        break
      case 'network':
        this.handleNetworkMessage(data)
        break
      case 'error':
        this.handleErrorMessage(data)
        break
      case 'performance':
        this.handlePerformanceMessage(data)
        break
      case 'dom':
        console.log('PreviewDevTools: Handling DOM message:', data)
        this.handleDomMessage(data)
        break
      
      // New postMessage response types
      case 'devtools-pong':
        console.log('DevTools: Received pong response')
        this.devToolsSupported = true
        break
        
      case 'dom-data':
        console.log('DevTools: Received DOM data')
        this.handleDomData(event.data)
        break
        
      case 'console-log':
        console.log('DevTools: Received console log')
        this.handleConsoleLog(event.data)
        break
        
      case 'network-request':
        console.log('DevTools: Received network request')
        this.handleNetworkRequest(event.data)
        break
        
      case 'network-error':
        console.log('DevTools: Received network error')
        this.handleNetworkError(event.data)
        break
        
      case 'javascript-error':
        console.log('DevTools: Received JavaScript error')
        this.handleJavaScriptError(event.data)
        break
        
      case 'performance-data':
        console.log('DevTools: Received performance data')
        this.handlePerformanceData(event.data)
        break
        
      case 'console-capture-enabled':
      case 'network-capture-enabled':
      case 'error-capture-enabled':
        console.log(`DevTools: ${type} confirmed`)
        break
        
      case 'devtools-error':
        console.error('DevTools: Error from target:', event.data.message)
        break
      
      case 'rails-error':
        console.log('🚨 DevTools: Received Rails error from error page')
        console.log('🚨 Rails error data:', event.data)
        this.handleRailsError(event.data)
        break
      
      default:
        console.debug('PreviewDevTools: Unknown message type:', type, data)
    }
  }

  validateMessageOrigin(origin) {
    // Allow messages from the iframe's origin
    try {
      const iframeOrigin = new URL(this.iframeTarget.src).origin
      return origin === iframeOrigin || origin === window.location.origin
    } catch (e) {
      console.error('Invalid origin validation:', e)
      return false
    }
  }

  handleConsoleMessage(data) {
    console.log('🚨 handleConsoleMessage called with:', data)
    this.dispatch('console-message', { detail: data })
    // Don't update badge count here - let console_log_controller manage it
    // this.updateBadgeCount('console', data.count)
    
    // Also trigger the event for console log component 
    const event = new CustomEvent('preview-devtools:console-message', {
      detail: data,
      bubbles: true
    })
    this.element.dispatchEvent(event)
  }

  handleNetworkMessage(data) {
    this.dispatch('network-message', { detail: data })
    this.updateBadgeCount('network', data.count)
  }

  handleErrorMessage(data) {
    this.dispatch('error-message', { detail: data })
    this.updateBadgeCount('errors', data.count)
  }

  handlePerformanceMessage(data) {
    this.dispatch('performance-message', { detail: data })
  }

  handleDomMessage(data) {
    this.dispatch('dom-message', { detail: data })
  }

  handleRailsError(eventData) {
    const errorData = eventData.data || eventData
    
    // Prevent duplicate processing of the same error
    const errorKey = `${errorData.error_class}:${errorData.message}:${errorData.path}:${errorData.timestamp}`
    if (this.processedRailsErrors && this.processedRailsErrors.has(errorKey)) {
      console.log('Skipping duplicate Rails error:', errorData)
      return
    }
    
    if (!this.processedRailsErrors) {
      this.processedRailsErrors = new Set()
    }
    this.processedRailsErrors.add(errorKey)
    
    // Mark that we have Rails errors - this prevents limited functionality warning
    this.hasRailsErrors = true
    
    console.log('Processing Rails error:', errorData)
    
    // Format the Rails error for display in console
    const errorMessage = `${errorData.error_class}: ${errorData.message}`
    
    // Create a detailed error log entry
    const errorLog = {
      level: 'error',
      message: errorMessage,
      source: errorData.error_location ? `${errorData.error_location.file}:${errorData.error_location.line}` : errorData.path,
      timestamp: errorData.timestamp || new Date().toISOString(),
      stack: errorData.backtrace ? errorData.backtrace.join('\n') : null,
      railsError: true,
      errorData: errorData // Store full error data for Claude integration
    }
    
    // Send Rails error to the Errors tab where it belongs
    console.log('🚨 Adding Rails error to Errors tab:', errorLog)
    this.addRailsErrorToErrorsTab(errorLog)
    
    // Also send to error tracker
    this.dispatch('error-message', { 
      detail: {
        ...errorLog,
        severity: 'error',
        context: {
          url: errorData.url,
          method: errorData.method,
          params: errorData.params,
          status: errorData.status,
          formattedTitle: errorData.formattedTitle,
          formattedMessage: errorData.formattedMessage,
          sourceExtract: errorData.sourceExtract
        }
      }
    })
    
    // Show a notification that a Rails error was captured
    this.showRailsErrorNotification(errorData)
  }
  
  showRailsErrorNotification(errorData) {
    // Create a temporary notification in the console panel
    const consolePanel = this.consolePanelTarget
    if (consolePanel) {
      const notification = document.createElement('div')
      notification.className = 'alert alert-error m-2'
      notification.innerHTML = `
        <div class="flex items-start gap-2">
          <i class="fas fa-exclamation-circle mt-0.5"></i>
          <div class="flex-1">
            <div class="font-semibold">Rails Error Captured</div>
            <div class="text-sm opacity-90">${errorData.error_class}: ${errorData.message}</div>
            <div class="text-xs opacity-75 mt-1">
              ${errorData.error_location ? `at ${errorData.error_location.file}:${errorData.error_location.line}` : ''}
            </div>
          </div>
          <button class="btn btn-xs btn-ghost" onclick="this.parentElement.parentElement.remove()">
            <i class="fas fa-times"></i>
          </button>
        </div>
      `
      
      // Insert at top of console panel
      consolePanel.insertBefore(notification, consolePanel.firstChild)
      
      // Auto-remove after 5 seconds
      setTimeout(() => {
        notification.remove()
      }, 5000)
    }
  }

  updateBadgeCount(panel, count) {
    const badgeTarget = `${panel}BadgeTarget`
    const hasTarget = this[`has${badgeTarget.charAt(0).toUpperCase() + badgeTarget.slice(1)}`]
    
    if (hasTarget && this[badgeTarget]) {
      this[badgeTarget].textContent = count > 0 ? count : ''
      this[badgeTarget].classList.toggle('hidden', count === 0)
    } else {
      // Badge target doesn't exist, which is fine - badges are conditionally rendered
      console.debug(`DevTools: No badge target for ${panel} (this is normal if count is 0)`)
    }
  }

  // New postMessage response handlers
  handleDomData(data) {
    console.log('DOM data received:', data)
    
    // Dispatch DOM data to components (local dispatch)
    this.dispatch('dom-data-received', { detail: {
      html: data.html,
      url: data.url,
      title: data.title,
      elements: data.elements,
      timestamp: data.timestamp
    }})
    
    // Also dispatch on document for DOM Inspector to catch
    const event = new CustomEvent('preview-devtools:dom-data-received', {
      detail: {
        html: data.html,
        url: data.url,
        title: data.title,
        elements: data.elements,
        timestamp: data.timestamp
      },
      bubbles: true
    })
    document.dispatchEvent(event)
    
    // Update DOM panel if it exists
    if (this.hasDomPanelTarget) {
      // The DOM panel should update via the event
      console.log('DOM panel should update with new data')
    }
  }

  handleConsoleLog(data) {
    console.log('Console log received:', data)
    
    // Don't track console count here - let console_log_controller manage it
    // this.consoleCount = (this.consoleCount || 0) + 1
    // this.updateBadgeCount('console', this.consoleCount)
    
    // Dispatch to console components
    this.dispatch('console-log-received', { detail: {
      level: data.level,
      args: data.args,
      timestamp: data.timestamp
    }})
    
    // Also dispatch for console log component
    const event = new CustomEvent('preview-devtools:console-message', {
      detail: {
        level: data.level,
        message: data.args ? data.args.join(' ') : '',
        timestamp: data.timestamp,
        count: 1
      },
      bubbles: true
    })
    this.element.dispatchEvent(event)
  }

  handleNetworkRequest(data) {
    console.log('Network request received:', data)
    
    // Track network count
    this.networkCount = (this.networkCount || 0) + 1
    this.updateBadgeCount('network', this.networkCount)
    
    // Dispatch to network components
    this.dispatch('network-request-received', { detail: {
      url: data.url,
      method: data.method,
      status: data.status,
      duration: data.duration,
      headers: data.headers,
      timestamp: data.timestamp
    }})
  }

  handleNetworkError(data) {
    console.log('Network error received:', data)
    
    // Track network count (errors also count as network activity)
    this.networkCount = (this.networkCount || 0) + 1
    this.updateBadgeCount('network', this.networkCount)
    
    // Dispatch to network components
    this.dispatch('network-error-received', { detail: {
      url: data.url,
      method: data.method,
      error: data.error,
      duration: data.duration,
      timestamp: data.timestamp
    }})
  }

  handleJavaScriptError(data) {
    console.log('JavaScript error received:', data)
    
    // Track error count
    this.errorCount = (this.errorCount || 0) + 1
    this.updateBadgeCount('errors', this.errorCount)
    
    // Dispatch to error components
    this.dispatch('javascript-error-received', { detail: {
      message: data.message,
      filename: data.filename,
      lineno: data.lineno,
      colno: data.colno,
      stack: data.stack,
      timestamp: data.timestamp
    }})
  }

  handlePerformanceData(data) {
    console.log('Performance data received:', data)
    
    // Dispatch to performance components (local)
    this.dispatch('performance-data-received', { detail: {
      navigation: data.navigation,
      resources: data.resources,
      memory: data.memory,
      timing: data.timing,
      timestamp: data.timestamp
    }})
    
    // Also dispatch on document for performance metrics controller
    const event = new CustomEvent('preview-devtools:performance-data-received', {
      detail: {
        navigation: data.navigation,
        resources: data.resources,
        memory: data.memory,
        timing: data.timing,
        timestamp: data.timestamp
      },
      bubbles: true
    })
    document.dispatchEvent(event)
  }

  initializePostMessageMonitoring() {
    // Prevent duplicate initialization
    if (this.monitoringInitialized) {
      console.log('DevTools monitoring already initialized, skipping...')
      return
    }
    
    console.log('Initializing postMessage-based monitoring...')
    
    if (!this.hasIframeTarget) {
      console.error('No iframe target found')
      return
    }

    try {
      const iframeWindow = this.iframeTarget.contentWindow
      
      if (!iframeWindow) {
        console.error('Cannot access iframe contentWindow')
        return
      }

      // Clear any existing intervals first
      this.teardownMonitoring()

      // Send monitoring setup commands
      this.sendMonitoringCommand('capture-console', { requestId: 'console-init' })
      this.sendMonitoringCommand('capture-network', { requestId: 'network-init' })
      this.sendMonitoringCommand('capture-errors', { requestId: 'errors-init' })
      
      // Start DOM monitoring
      this.startDOMMonitoring()
      
      // Start performance monitoring
      this.startPerformanceMonitoring()
      
      // Mark as initialized
      this.monitoringInitialized = true
      
      console.log('DevTools postMessage monitoring initialized successfully')
    } catch (error) {
      console.error('Failed to initialize postMessage monitoring:', error.message)
      console.error('Error details:', error)
    }
  }

  sendMonitoringCommand(command, data = {}) {
    try {
      this.iframeTarget.contentWindow.postMessage({
        type: command,
        ...data,
        timestamp: Date.now()
      }, '*')
      this.logger.debug(`Sent ${command} command`)
    } catch (error) {
      console.error(`Failed to send ${command} command:`, error)
    }
  }

  startDOMMonitoring() {
    // Request initial DOM snapshot
    this.sendMonitoringCommand('capture-dom', { requestId: 'dom-initial' })
    
    // Set up periodic DOM snapshots (every 5 seconds)
    if (this.domMonitoringInterval) {
      clearInterval(this.domMonitoringInterval)
    }
    
    this.domMonitoringInterval = setInterval(() => {
      this.sendMonitoringCommand('capture-dom', { requestId: 'dom-periodic' })
    }, 5000)
  }

  startPerformanceMonitoring() {
    // Request initial performance metrics
    this.sendMonitoringCommand('get-performance', { requestId: 'perf-initial' })
    
    // Set up periodic performance monitoring (every 10 seconds)
    if (this.performanceMonitoringInterval) {
      clearInterval(this.performanceMonitoringInterval)
    }
    
    this.performanceMonitoringInterval = setInterval(() => {
      this.sendMonitoringCommand('get-performance', { requestId: 'perf-periodic' })
    }, 10000)
  }


  switchPanel(event) {
    const panel = event.currentTarget.dataset.panel
    console.log('Switching to panel:', panel)
    this.showPanel(panel)
  }

  showPanel(panelName) {
    console.log('Showing panel:', panelName)
    
    // Hide all panels
    if (this.hasConsolePanelTarget) this.consolePanelTarget.classList.add('hidden')
    if (this.hasNetworkPanelTarget) this.networkPanelTarget.classList.add('hidden')
    if (this.hasDomPanelTarget) this.domPanelTarget.classList.add('hidden')
    if (this.hasPerformancePanelTarget) this.performancePanelTarget.classList.add('hidden')
    if (this.hasErrorsPanelTarget) this.errorsPanelTarget.classList.add('hidden')

    // Show selected panel
    const panelTarget = `${panelName}PanelTarget`
    if (this[`has${panelTarget.charAt(0).toUpperCase() + panelTarget.slice(1)}`] && this[panelTarget]) {
      console.log('Showing target:', panelTarget)
      this[panelTarget].classList.remove('hidden')
    } else {
      console.error('Panel target not found:', panelTarget)
    }

    // Update tab states
    if (this.hasTabTargets) {
      this.tabTargets.forEach(tab => {
        if (tab.dataset.panel === panelName) {
          tab.classList.add('tab-active')
        } else {
          tab.classList.remove('tab-active')
        }
      })
    }

    this.dispatch('panel-switched', { detail: { panel: panelName } })
  }

  clearCurrentPanel() {
    const activePanel = this.tabTargets.find(tab => tab.classList.contains('tab-active'))?.dataset.panel
    console.log('Clearing panel:', activePanel)
    
    if (activePanel) {
      // Clear the panel content based on type
      switch(activePanel) {
        case 'console':
          if (this.hasConsolePanelTarget) {
            const logsContainer = this.consolePanelTarget.querySelector('[data-console-log-target="logsContainer"]')
            if (logsContainer) {
              logsContainer.innerHTML = ''
            }
          }
          // Don't reset count here - let console_log_controller manage it
          // this.consoleCount = 0
          // this.updateBadgeCount('console', 0)
          break
          
        case 'network':
          if (this.hasNetworkPanelTarget) {
            const networkTable = this.networkPanelTarget.querySelector('tbody')
            if (networkTable) {
              networkTable.innerHTML = ''
            }
          }
          this.networkCount = 0
          this.updateBadgeCount('network', 0)
          break
          
        case 'errors':
          if (this.hasErrorsPanelTarget) {
            const errorsTable = this.errorsPanelTarget.querySelector('tbody')
            if (errorsTable) {
              errorsTable.innerHTML = ''
            }
          }
          this.errorCount = 0
          this.updateBadgeCount('errors', 0)
          // Clear processed Rails errors so they can be re-added if they occur again
          this.processedRailsErrors = new Set()
          break
          
        case 'dom':
          if (this.hasDomPanelTarget) {
            const domContent = this.domPanelTarget.querySelector('[data-dom-inspector-target="domContent"]')
            if (domContent) {
              domContent.innerHTML = '<div class="text-center text-base-content/50 py-8">No DOM snapshot available</div>'
            }
          }
          break
          
        case 'performance':
          if (this.hasPerformancePanelTarget) {
            // Performance metrics are usually real-time, so we might not want to clear them
            console.log('Performance panel clear not implemented - metrics are real-time')
          }
          break
      }
      
      // Also dispatch the clear event for any external listeners
      this.dispatch(`clear-${activePanel}`, {})
    }
  }

  // Removed toggle and resize functionality as the UI is now fixed

  async checkDevToolsSupport() {
    console.log('Checking DevTools support via postMessage...')
    
    // Skip the check for error pages - they won't have DevTools but that's OK
    try {
      const iframeDoc = this.iframeTarget.contentDocument || this.iframeTarget.contentWindow?.document
      if (iframeDoc) {
        const bodyText = iframeDoc.body?.innerText || ''
        const titleText = iframeDoc.title || ''
        
        // Check if this is a Rails error page
        if (titleText.includes('Error') || 
            titleText.includes('Exception') ||
            bodyText.includes('Routing Error') || 
            bodyText.includes('ActionController::') ||
            bodyText.includes('Rails.root:')) {
          console.log('Rails error page detected - skipping DevTools check')
          return true // Pretend it's supported to avoid showing the config message
        }
      }
    } catch (e) {
      // Cross-origin restriction - continue with normal check
    }
    
    // Method 1: Check via HTTP headers first
    try {
      const response = await fetch(this.iframeTarget.src, { method: 'HEAD' })
      const devtoolsEnabled = response.headers.get('DevTools-Enabled')
      if (devtoolsEnabled === 'true') {
        console.log('DevTools support detected via HTTP headers')
        return true
      }
    } catch (error) {
      console.warn('Could not check DevTools headers:', error)
    }
    
    // Method 2: Use postMessage ping-pong to detect DevTools support
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        console.log('DevTools ping timeout - no response from iframe')
        resolve(false)
      }, 2000)
      
      // Listen for DevTools response
      const handleMessage = (event) => {
        if (event.origin !== new URL(this.iframeTarget.src).origin) return
        if (event.data?.type === 'devtools-pong') {
          console.log('DevTools support confirmed via postMessage')
          clearTimeout(timeout)
          window.removeEventListener('message', handleMessage)
          resolve(true)
        }
      }
      
      window.addEventListener('message', handleMessage)
      
      // Send ping to iframe
      setTimeout(() => {
        console.log('Sending DevTools ping to iframe...')
        this.iframeTarget.contentWindow.postMessage({
          type: 'devtools-ping',
          timestamp: Date.now()
        }, '*')
      }, 500)
    })
  }
  
  showConfigurationMessage() {
    // Don't show limited functionality message if we have captured Rails errors
    if (this.hasRailsErrors) {
      console.log('🚨 Skipping limited functionality warning - Rails errors captured')
      return
    }
    
    // Show configuration instructions in DevTools panels
    const message = `
      <div class="alert alert-warning">
        <div class="flex">
          <svg class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <div>
            <h3 class="font-bold">Limited DevTools Functionality</h3>
            <div class="text-xs mt-1">
              DevTools monitoring is not available on this page. This is normal for error pages.
              <br><br>
              For full DevTools support on regular pages, ensure your target application includes the DevTools monitoring script.
            </div>
          </div>
        </div>
      </div>
    `
    
    // Show in panel containers, but preserve existing content
    const panels = ['console', 'network', 'dom', 'performance', 'errors']
    panels.forEach(panel => {
      const panelTarget = `${panel}PanelTarget`
      if (this[`has${panelTarget.charAt(0).toUpperCase() + panelTarget.slice(1)}`]) {
        const existing = this[panelTarget].innerHTML
        // Only show warning if panel is empty (no existing content)
        if (!existing || existing.trim() === '') {
          this[panelTarget].innerHTML = message
        }
      }
    })
  }
  
  addRailsErrorToErrorsTab(errorLog) {
    // Add Rails error to the Errors tab as a proper table
    if (this.hasErrorsPanelTarget) {
      // Initialize errors table if it doesn't exist
      let errorsTable = this.errorsPanelTarget.querySelector('.errors-table')
      if (!errorsTable) {
        this.errorsPanelTarget.innerHTML = `
          <div class="bg-base-100 border border-base-200 rounded-lg">
            <table class="table table-sm errors-table">
              <thead>
                <tr>
                  <th class="w-24">Status</th>
                  <th>Error</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody></tbody>
            </table>
          </div>
        `
        errorsTable = this.errorsPanelTarget.querySelector('.errors-table')
      }
      
      const tbody = errorsTable.querySelector('tbody')
      const timestamp = new Date(errorLog.timestamp).toLocaleTimeString()
      const errorId = Date.now() + Math.random()
      
      const errorRow = `
        <tr class="hover" data-error-id="${errorId}">
          <td class="text-center">
            <div class="flex items-center justify-center gap-1">
              <div class="badge badge-error badge-sm">
                <i class="fas fa-exclamation-triangle text-xs"></i>
                ${errorLog.errorData?.status || 500}
              </div>
            </div>
          </td>
          <td>
            <div class="flex flex-col">
              <span class="font-medium text-sm truncate max-w-xs" title="${errorLog.message}">
                ${errorLog.message}
              </span>
              <div class="flex items-center gap-2 mt-1">
                <span class="badge badge-xs badge-neutral">${errorLog.errorData?.method || 'GET'}</span>
                <span class="text-xs text-base-content/60">${errorLog.source}</span>
                <span class="text-xs text-base-content/50">${timestamp}</span>
              </div>
            </div>
          </td>
          <td>
            <div class="flex items-center gap-1">
              <button class="btn btn-ghost btn-sm btn-square"
                      data-action="click->preview-devtools#sendErrorToClaude"
                      data-error-data='${JSON.stringify(errorLog)}'
                      title="Send to Claude">
                <i class="fas fa-robot text-primary text-base"></i>
              </button>
              <button class="btn btn-ghost btn-sm btn-square"
                      data-action="click->preview-devtools#showErrorDetails"
                      data-error-data='${JSON.stringify(errorLog)}'
                      title="View Details">
                <i class="fas fa-eye text-info text-base"></i>
              </button>
            </div>
          </td>
        </tr>
      `
      
      // Add to top of errors table
      tbody.insertAdjacentHTML('afterbegin', errorRow)
      
      // Update errors badge count
      const errorCount = tbody.children.length
      this.updateBadgeCount('errors', errorCount)
    }
  }
  
  sendErrorToClaude(event) {
    const errorData = JSON.parse(event.currentTarget.dataset.errorData)
    console.log('Sending Rails error to Claude using story execution pattern:', errorData)
    
    // Format error message for Claude (same format as story execution)
    const errorMessage = `I encountered a Rails server error that needs debugging:

**Error Details:**
- **Type:** ${errorData.message}
- **Source:** ${errorData.source}
- **Method:** ${errorData.errorData?.method || 'GET'}
- **Status:** ${errorData.errorData?.status || 500}
- **URL:** ${errorData.errorData?.url || 'N/A'}
- **Timestamp:** ${errorData.timestamp}

${errorData.stack ? `**Stack Trace:**
\`\`\`
${errorData.stack}
\`\`\`` : ''}

${errorData.errorData?.params ? `**Request Parameters:**
\`\`\`json
${JSON.stringify(errorData.errorData.params, null, 2)}
\`\`\`` : ''}

Please help me understand what's causing this error and how to fix it.`

    // Use the exact same pattern as story execution
    this.findAndSendToClaudeCode(errorMessage)
  }
  
  findAndSendToClaudeCode(message) {
    // Copied from task_execution_controller.js - exact same pattern
    const allChatComponents = document.querySelectorAll('[data-controller*="claude-code-chat"]')
    console.log('Found chat components:', allChatComponents.length)
    
    let chatComponent = null
    let messageInput = null
    
    // Find visible component
    for (const component of allChatComponents) {
      if (component.style.display === 'none' || component.closest('.hidden')) continue
      
      const input = component.querySelector('[data-claude-code-chat-target*="input"]')
      if (input && input.offsetParent !== null) {
        chatComponent = component
        messageInput = input
        break
      }
    }
    
    if (chatComponent && messageInput) {
      // Insert the error message
      messageInput.value = message
      messageInput.focus()
      
      // Try to submit the form (exact same pattern as story execution)
      const form = messageInput.closest('form')
      if (form) {
        const submitEvent = new SubmitEvent('submit', { 
          bubbles: true, 
          cancelable: true,
          submitter: form.querySelector('button[type="submit"]')
        })
        form.dispatchEvent(submitEvent)
        this.showNotification('Rails error sent to Claude Code for analysis', 'success')
      } else {
        this.showNotification('Claude Code input found, but could not submit form', 'warning')
      }
    } else {
      this.showNotification('Claude Code chat interface not found. Please initialize Claude Code first.', 'warning')
    }
  }
  
  showErrorDetails(event) {
    const errorData = JSON.parse(event.currentTarget.dataset.errorData)
    console.log('Showing error details:', errorData)
    
    // Create modal with error details
    const modal = document.createElement('dialog')
    modal.className = 'modal'
    modal.innerHTML = `
      <div class="modal-box max-w-4xl">
        <form method="dialog">
          <button class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">✕</button>
        </form>
        
        <h3 class="font-bold text-lg mb-4">
          <i class="fas fa-bug text-error mr-2"></i>
          Rails Error Details
        </h3>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div class="card bg-base-100 border border-base-300">
            <div class="card-body">
              <h4 class="card-title text-sm">Error Information</h4>
              <div class="space-y-2 text-sm">
                <div><strong>Type:</strong> ${errorData.errorData?.error_class || 'Unknown'}</div>
                <div><strong>Status:</strong> ${errorData.errorData?.status || 500}</div>
                <div><strong>Method:</strong> ${errorData.errorData?.method || 'GET'}</div>
                <div><strong>Path:</strong> ${errorData.source}</div>
                <div><strong>Time:</strong> ${new Date(errorData.timestamp).toLocaleString()}</div>
              </div>
            </div>
          </div>
          
          <div class="card bg-base-100 border border-base-300">
            <div class="card-body">
              <h4 class="card-title text-sm">Request Details</h4>
              <div class="space-y-2 text-sm">
                <div><strong>URL:</strong> <code class="text-xs">${errorData.errorData?.url || 'N/A'}</code></div>
                <div><strong>Referrer:</strong> <code class="text-xs">${errorData.errorData?.referrer || 'None'}</code></div>
                ${errorData.errorData?.params ? `<div><strong>Params:</strong> <pre class="text-xs mt-1 bg-base-200 p-2 rounded">${JSON.stringify(errorData.errorData.params, null, 2)}</pre></div>` : ''}
              </div>
            </div>
          </div>
        </div>
        
        <div class="card bg-error/5 border border-error/20">
          <div class="card-body">
            <h4 class="card-title text-sm text-error">Error Message</h4>
            <div class="font-mono text-sm bg-base-200 p-3 rounded">
              ${errorData.message}
            </div>
            ${errorData.stack ? `
              <h4 class="card-title text-sm text-error mt-4">Stack Trace</h4>
              <div class="font-mono text-xs bg-base-200 p-3 rounded overflow-auto max-h-64">
                ${errorData.stack}
              </div>
            ` : ''}
          </div>
        </div>
        
        <div class="modal-action">
          <button class="btn btn-primary" onclick="navigator.clipboard.writeText('${JSON.stringify(errorData, null, 2).replace(/'/g, "\\'")}')">
            <i class="fas fa-copy mr-2"></i>
            Copy Error Data
          </button>
          <form method="dialog">
            <button class="btn">Close</button>
          </form>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop">
        <button>close</button>
      </form>
    `
    
    document.body.appendChild(modal)
    modal.showModal()
    
    // Remove modal when closed
    modal.addEventListener('close', () => {
      document.body.removeChild(modal)
    })
  }
  
  showNotification(message, type = 'info') {
    // Create toast notification
    const toast = document.createElement('div')
    toast.className = `alert alert-${type} fixed top-4 right-4 w-auto max-w-sm z-50 opacity-0 translate-x-4 transition-all duration-300`
    toast.innerHTML = `
      <div class="flex items-center gap-2">
        <i class="fas ${type === 'success' ? 'fa-check-circle' : type === 'error' ? 'fa-exclamation-circle' : 'fa-info-circle'}"></i>
        <span>${message}</span>
      </div>
    `
    
    document.body.appendChild(toast)
    
    // Animate in
    setTimeout(() => {
      toast.classList.remove('opacity-0', 'translate-x-4')
    }, 10)
    
    // Remove after 3 seconds
    setTimeout(() => {
      toast.classList.add('opacity-0', 'translate-x-4')
      setTimeout(() => {
        document.body.removeChild(toast)
      }, 300)
    }, 3000)
  }

  testConsoleIntegration() {
    console.log('Testing console integration...')
    
    // Simulate some console messages
    setTimeout(() => {
      this.handleConsoleMessage({
        detail: {
          level: 'info',
          message: 'DevTools initialized successfully',
          timestamp: new Date().toISOString(),
          source: 'devtools-test'
        }
      })
    }, 500)
    
    setTimeout(() => {
      this.handleConsoleMessage({
        detail: {
          level: 'log',
          message: 'Console panel is working!',
          timestamp: new Date().toISOString(),
          source: 'devtools-test'
        }
      })
    }, 1000)
  }

  // Browser Navigation Methods
  initializeNavigation() {
    // Initialize navigation history with the initial URL
    if (this.iframeUrlValue) {
      this.navigationHistoryValue = [this.iframeUrlValue]
      this.historyIndexValue = 0
    }
    
    // Update the navigation buttons initial state
    this.updateNavigationButtons()
  }

  navigateBack() {
    if (this.historyIndexValue > 0) {
      this.historyIndexValue--
      const url = this.navigationHistoryValue[this.historyIndexValue]
      this.loadUrl(url, false) // Don't add to history when navigating back
    }
    this.updateNavigationButtons()
  }

  navigateForward() {
    if (this.historyIndexValue < this.navigationHistoryValue.length - 1) {
      this.historyIndexValue++
      const url = this.navigationHistoryValue[this.historyIndexValue]
      this.loadUrl(url, false) // Don't add to history when navigating forward
    }
    this.updateNavigationButtons()
  }

  refreshIframe() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.remove('hidden')
    }
    
    // Reload the current iframe URL
    const currentSrc = this.iframeTarget.src
    this.iframeTarget.src = currentSrc
  }

  navigateToUrl(event) {
    event.preventDefault()
    const url = this.urlBarTarget.value.trim()
    
    if (!url) return
    
    // Add protocol if missing
    let fullUrl = url
    if (!url.match(/^https?:\/\//)) {
      // If it looks like a local path, use the current origin
      if (url.startsWith('/')) {
        fullUrl = new URL(url, window.location.origin).href
      } else {
        fullUrl = 'https://' + url
      }
    }
    
    this.loadUrl(fullUrl, true)
  }

  loadUrl(url, addToHistory = true) {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.remove('hidden')
    }
    
    // Update the iframe
    this.iframeTarget.src = url
    
    // Update the URL bar
    if (this.hasUrlBarTarget) {
      this.urlBarTarget.value = url
    }
    
    // Add to history if navigating to a new URL
    if (addToHistory) {
      // Remove any forward history when navigating to a new URL
      this.navigationHistoryValue = this.navigationHistoryValue.slice(0, this.historyIndexValue + 1)
      this.navigationHistoryValue.push(url)
      this.historyIndexValue = this.navigationHistoryValue.length - 1
    }
    
    this.updateNavigationButtons()
  }

  onIframeLoad() {
    // Hide loading indicator
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.add('hidden')
    }
    
    // Try to get the actual URL from the iframe (may fail due to cross-origin)
    try {
      const iframeUrl = this.iframeTarget.contentWindow.location.href
      if (this.hasUrlBarTarget && iframeUrl !== 'about:blank') {
        this.urlBarTarget.value = iframeUrl
        
        // Update history if this is a new URL (e.g., from navigation within the iframe)
        const currentHistoryUrl = this.navigationHistoryValue[this.historyIndexValue]
        if (iframeUrl !== currentHistoryUrl) {
          this.loadUrl(iframeUrl, true)
        }
      }
    } catch (e) {
      // Cross-origin restriction - can't access iframe URL
      console.debug('Cannot access iframe URL due to cross-origin restrictions')
    }
    
    // Re-initialize DevTools monitoring for the new page
    this.initializePostMessageMonitoring()
  }

  updateNavigationButtons() {
    // Update back button state
    if (this.hasBackButtonTarget) {
      this.backButtonTarget.disabled = this.historyIndexValue <= 0
      if (this.historyIndexValue <= 0) {
        this.backButtonTarget.classList.add('btn-disabled')
      } else {
        this.backButtonTarget.classList.remove('btn-disabled')
      }
    }
    
    // Update forward button state
    if (this.hasForwardButtonTarget) {
      this.forwardButtonTarget.disabled = this.historyIndexValue >= this.navigationHistoryValue.length - 1
      if (this.historyIndexValue >= this.navigationHistoryValue.length - 1) {
        this.forwardButtonTarget.classList.add('btn-disabled')
      } else {
        this.forwardButtonTarget.classList.remove('btn-disabled')
      }
    }
  }
}